// Interactive chat blocks — the clickable UI the assistant renders
// instead of prose questions.
//
// The backend's interactive UI tools (ask_user_choice, request_file,
// offer_samples, connect_source, show_checklist) each emit an
// `attachment` block with a matching kind; this module renders them.
// The contract with the thread:
//
//   interactive = {
//     enabled:    bool,          // only the newest assistant turn is live
//     chosenText: string|null,   // next user message (highlights the pick)
//     onSend:     (text, files?) => void,   // feeds the chat stream
//   }
//
// Clicks become regular user messages (the option label / a short
// summary), so the model sees exactly what the user did. Anything
// sensitive (sample imports, connector credentials, file uploads) is
// executed by the app against the existing ingestion endpoints and
// only a summary sentence goes back through the LLM.

import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box, Stack, Typography, TextField, Checkbox, Chip, Switch,
  LinearProgress, CircularProgress, Button, Slider, IconButton, alpha,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import { keyframes } from '@mui/system';
import {
  ShowChart as ChartIcon,
  Flag as GoalIcon,
  NotificationsActive as AlertIcon,
  Storage as DatabaseIcon,
  DescriptionOutlined as FileIcon,
  CloudQueue as CloudIcon,
  Power as PlugIcon,
  AutoAwesome as SparkleIcon,
  Schedule as ClockIcon,
  Language as GlobeIcon,
  ShoppingCart as CartIcon,
  Payments as MoneyIcon,
  PrecisionManufacturing as FactoryIcon,
  LocalHospital as HealthIcon,
  Bolt as EnergyIcon,
  Security as SecurityIcon,
  WbSunny as WeatherIcon,
  DirectionsBus as TransportIcon,
  SportsEsports as GameIcon,
  HomeWork as HomeIcon,
  Science as FlaskIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as TodoIcon,
  CloudUpload as UploadIcon,
  Check as CheckIcon,
  OpenInNew as OpenInNewIcon,
  ErrorOutline as ErrorIcon,
  Tune as TuneIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  ContentCopy as CopyIcon,
  TrackChanges as TargetIcon,
  ArrowForward as ArrowIcon,
  Terminal as TerminalIcon,
} from '@mui/icons-material';

import {
  fetchRequest as loadSampleRequest,
  clear as clearLoadSample,
} from '../../../redux/actions/datasources/loadSample';
import {
  fetchRequest as connectInspectRequest,
  clear as clearConnectInspect,
} from '../../../redux/actions/datasources/connectInspect';
import {
  fetchRequest as connectCreateRequest,
  clear as clearConnectCreate,
} from '../../../redux/actions/datasources/connectCreate';
import { USE_CASES, getUseCaseById } from '../../../data/sampleData';
import config from '../../../config';
import { surfacesFor } from './uiTokens';
import { useAnalystDock } from '../../analyst/dockState';


export const INTERACTIVE_KINDS = new Set([
  'options', 'file_drop', 'checklist', 'sample_picker', 'connector_setup',
  'config_form', 'correlation_bars', 'goal_overview', 'goal_api',
  'goal_pitch', // legacy name for goal_overview, kept for stored chats
]);


const blockIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: none; }
`;
const enterSx = { animation: `${blockIn} 260ms cubic-bezier(0.2, 0.7, 0.3, 1) both` };


// Backend icon names (tools.UI_ICONS) -> MUI icons. Unknown -> dot.
const ICONS = {
  chart: ChartIcon, goal: GoalIcon, alert: AlertIcon, database: DatabaseIcon,
  file: FileIcon, cloud: CloudIcon, plug: PlugIcon, sparkle: SparkleIcon,
  clock: ClockIcon, globe: GlobeIcon, cart: CartIcon, money: MoneyIcon,
  factory: FactoryIcon, health: HealthIcon, energy: EnergyIcon,
  security: SecurityIcon, weather: WeatherIcon, transport: TransportIcon,
  game: GameIcon, home: HomeIcon, flask: FlaskIcon,
};

const accentOf = (t) => (t.palette.mode === 'dark'
  ? (t.brand?.lime || t.palette.primary.main)
  : t.palette.primary.main);


const PromptLine = ({ children }) => (
  children ? (
    <Typography sx={(t) => ({
      fontSize: 13, fontWeight: 600, mb: 1, color: surfacesFor(t).text,
    })}>
      {children}
    </Typography>
  ) : null
);


// ---------------------------------------------------------------------------
// options — clickable chips / cards
// ---------------------------------------------------------------------------

const OptionChip = ({ opt, picked, dim, onPick, disabled }) => (
  <Box
    role="button"
    tabIndex={disabled ? -1 : 0}
    onClick={disabled ? undefined : onPick}
    onKeyDown={(e) => { if (!disabled && e.key === 'Enter') onPick(); }}
    sx={(t) => {
      const s = surfacesFor(t);
      const accent = accentOf(t);
      return {
        px: 1.75, py: 0.8, borderRadius: 999,
        display: 'inline-flex', alignItems: 'center', gap: 0.75,
        border: `1px solid ${picked ? accent : s.border2}`,
        bgcolor: picked ? alpha(accent, t.palette.mode === 'dark' ? 0.14 : 0.08) : 'transparent',
        fontSize: 12.5, fontWeight: picked ? 700 : 500,
        color: picked ? accent : s.muted,
        cursor: disabled ? 'default' : 'pointer',
        userSelect: 'none',
        opacity: dim ? 0.45 : 1,
        transition: 'all 120ms',
        ...(disabled ? {} : {
          '&:hover': {
            borderColor: t.brand?.teal || t.palette.primary.main,
            color: accent,
          },
        }),
      };
    }}
  >
    {picked && <CheckIcon sx={{ fontSize: 13 }} />}
    {opt.label}
  </Box>
);

const OptionCard = ({ opt, picked, dim, onPick, disabled }) => {
  const Icon = ICONS[opt.icon] || SparkleIcon;
  return (
    <Box
      role="button"
      tabIndex={disabled ? -1 : 0}
      onClick={disabled ? undefined : onPick}
      onKeyDown={(e) => { if (!disabled && e.key === 'Enter') onPick(); }}
      sx={(t) => {
        const s = surfacesFor(t);
        const accent = accentOf(t);
        return {
          position: 'relative',
          p: '12px 14px', borderRadius: '14px', textAlign: 'left',
          border: `1.5px solid ${picked ? accent : s.border}`,
          bgcolor: picked
            ? alpha(accent, t.palette.mode === 'dark' ? 0.10 : 0.05)
            : s.panel,
          cursor: disabled ? 'default' : 'pointer',
          userSelect: 'none',
          opacity: dim ? 0.45 : 1,
          transition: 'all 130ms ease',
          ...(disabled ? {} : {
            '&:hover': {
              borderColor: accent,
              transform: 'translateY(-1px)',
              boxShadow: `0 6px 18px -6px ${alpha(accent, 0.35)}`,
            },
          }),
        };
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        <Box sx={(t) => {
          const accent = accentOf(t);
          return {
            width: 30, height: 30, borderRadius: '9px', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: alpha(accent, t.palette.mode === 'dark' ? 0.16 : 0.1),
            color: accent,
          };
        }}>
          <Icon sx={{ fontSize: 16 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.25 }}>
            <Typography sx={(t) => ({
              fontSize: 12.5, fontWeight: 700, lineHeight: 1.3,
              color: surfacesFor(t).text,
            })}>
              {opt.label}
            </Typography>
            {opt.badge && (
              <Chip
                size="small" label={opt.badge}
                sx={(t) => ({
                  height: 16, fontSize: 9, fontWeight: 700,
                  bgcolor: alpha(accentOf(t), 0.14), color: accentOf(t),
                })}
              />
            )}
          </Stack>
          {opt.description && (
            <Typography sx={(t) => ({
              mt: 0.25, fontSize: 11, lineHeight: 1.4,
              color: surfacesFor(t).muted,
            })}>
              {opt.description}
            </Typography>
          )}
        </Box>
        {picked && (
          <CheckCircleIcon sx={(t) => ({ fontSize: 16, color: accentOf(t), flexShrink: 0 })} />
        )}
      </Stack>
    </Box>
  );
};

const OptionsBlock = ({ spec, interactive }) => {
  const { enabled, chosenText, onSend } = interactive || {};
  const navigate = useNavigate();
  const { openDock, collapseDock } = useAnalystDock();
  const options = Array.isArray(spec?.options) ? spec.options : [];
  const picked = (chosenText || '').trim();
  const pickedId = options.find((o) => o.label === picked)?.id || null;
  const answered = Boolean(pickedId);
  const disabled = !enabled;

  // Options that ARE a navigation (backend resolved `goto` to a client
  // path) open the page on pick. List pages keep the chat docked beside
  // them; entity pages (a goal's alerts/scenarios/forecast) carry
  // `minimize` — the chat collapses so the product UI leads. The pick
  // still goes to the model as the user's message either way.
  const pick = (o) => {
    onSend?.(o.label);
    if (o.goto_path) {
      if (o.minimize) collapseDock(); else openDock();
      navigate(o.goto_path);
    }
  };

  const cards = spec?.style === 'cards';
  return (
    <Box sx={{ my: 1, ...enterSx }}>
      <PromptLine>{spec?.prompt}</PromptLine>
      {cards ? (
        <Box sx={{
          display: 'grid', gap: 1,
          // Sized by the CONTAINER, not the viewport, so the same block
          // lays out 3-up on the full page and 1-up in the docked
          // sidecar (viewport breakpoints would lie there).
          gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
        }}>
          {options.map((o) => (
            <OptionCard
              key={o.id || o.label}
              opt={o}
              picked={o.label === picked}
              dim={answered && o.label !== picked}
              disabled={disabled}
              onPick={() => pick(o)}
            />
          ))}
        </Box>
      ) : (
        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
          {options.map((o) => (
            <OptionChip
              key={o.id || o.label}
              opt={o}
              picked={o.label === picked}
              dim={answered && o.label !== picked}
              disabled={disabled}
              onPick={() => pick(o)}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};


// ---------------------------------------------------------------------------
// file_drop — inline CSV drop zone (reuses the composer's upload path)
// ---------------------------------------------------------------------------

const FileDropBlock = ({ spec, interactive }) => {
  const { enabled, onSend } = interactive || {};
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [err, setErr] = useState(null);

  const handleFiles = (fileList) => {
    const file = Array.from(fileList || []).filter(Boolean)[0];
    if (!file) return;
    if (!/\.csv$/i.test(file.name)) {
      setErr(`${file.name} isn't a CSV — only .csv files are supported here.`);
      return;
    }
    setErr(null);
    onSend?.(`Uploaded ${file.name}.`, [file]);
  };

  return (
    <Box sx={{ my: 1, maxWidth: 440, ...enterSx }}>
      <Box
        onClick={enabled ? () => inputRef.current?.click() : undefined}
        onDragOver={enabled ? (e) => { e.preventDefault(); setDragOver(true); } : undefined}
        onDragLeave={enabled ? () => setDragOver(false) : undefined}
        onDrop={enabled ? (e) => {
          e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files);
        } : undefined}
        sx={(t) => {
          const s = surfacesFor(t);
          const accent = accentOf(t);
          return {
            p: '20px 18px', textAlign: 'center',
            borderRadius: '14px',
            border: `1.5px dashed ${dragOver ? accent : s.border2}`,
            bgcolor: dragOver ? alpha(accent, 0.06) : s.panel,
            cursor: enabled ? 'pointer' : 'default',
            opacity: enabled ? 1 : 0.55,
            transition: 'all 130ms',
            ...(enabled ? { '&:hover': { borderColor: accent } } : {}),
          };
        }}
      >
        <input
          ref={inputRef} type="file" hidden accept=".csv"
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
        />
        <UploadIcon sx={(t) => ({ fontSize: 26, color: accentOf(t), mb: 0.5 })} />
        <Typography sx={(t) => ({ fontSize: 13, fontWeight: 700, color: surfacesFor(t).text })}>
          {spec?.prompt || 'Drop a CSV file here'}
        </Typography>
        <Typography sx={(t) => ({ mt: 0.25, fontSize: 11, color: surfacesFor(t).muted })}>
          {spec?.hint || 'or click to browse — one row per time period works best'}
        </Typography>
      </Box>
      {err && (
        <Typography sx={{ mt: 0.75, fontSize: 11.5, color: 'error.main' }}>{err}</Typography>
      )}
    </Box>
  );
};


// ---------------------------------------------------------------------------
// checklist — read-only progress
// ---------------------------------------------------------------------------

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(94, 230, 208, 0.35); }
  50%      { box-shadow: 0 0 0 5px rgba(94, 230, 208, 0); }
`;

const ChecklistBlock = ({ spec }) => {
  const items = Array.isArray(spec?.items) ? spec.items : [];
  return (
    <Box
      sx={(t) => {
        const s = surfacesFor(t);
        return {
          my: 1, p: '14px 16px', maxWidth: 440,
          borderRadius: '14px',
          border: `1px solid ${s.border}`,
          bgcolor: s.panel,
          ...enterSx,
        };
      }}
    >
      {spec?.title && (
        <Typography sx={(t) => ({
          fontSize: 11, fontWeight: 700, letterSpacing: '0.07em',
          textTransform: 'uppercase', mb: 1.25, color: surfacesFor(t).muted,
        })}>
          {spec.title}
        </Typography>
      )}
      <Stack spacing={1}>
        {items.map((it, i) => {
          const done = it.status === 'done';
          const active = it.status === 'active';
          return (
            <Stack key={i} direction="row" spacing={1.25} alignItems="flex-start">
              {done ? (
                <CheckCircleIcon sx={(t) => ({ fontSize: 17, mt: '1px', color: accentOf(t) })} />
              ) : active ? (
                <Box sx={(t) => ({
                  width: 11, height: 11, mt: '4px', ml: '3px', mr: '3px',
                  borderRadius: '50%', flexShrink: 0,
                  bgcolor: t.brand?.teal || t.palette.primary.main,
                  animation: `${pulse} 1.8s ease-out infinite`,
                })} />
              ) : (
                <TodoIcon sx={(t) => ({ fontSize: 17, mt: '1px', color: surfacesFor(t).faint })} />
              )}
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={(t) => ({
                  fontSize: 12.5, lineHeight: 1.45,
                  fontWeight: active ? 700 : 500,
                  color: done || active ? surfacesFor(t).text : surfacesFor(t).muted,
                  textDecoration: 'none',
                })}>
                  {it.label}
                </Typography>
                {it.detail && (
                  <Typography sx={(t) => ({ fontSize: 11, color: surfacesFor(t).faint })}>
                    {it.detail}
                  </Typography>
                )}
              </Box>
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
};


// ---------------------------------------------------------------------------
// sample_picker — the get-data-in step: upload / connect / sample as
// three EQUAL paths (their own data first-class, samples for whoever
// has none handy), plus the curated dataset grid behind the sample path.
// ---------------------------------------------------------------------------
// import receipt — shown the moment an import completes, together with
// the tail of the data itself. There is no fork and nothing to decide:
// the import summary is sent to the assistant automatically, which
// confirms the target and builds the baseline (show first, ask later).
// ---------------------------------------------------------------------------

// Compact tail preview of the imported rows, rendered inside the
// completion receipt so "your data is in" is backed by the data itself.
// Columns come straight from the CSV header; rows are positional arrays
// (the newest slice of exactly what was ingested).

const fmtPreviewCell = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'number') {
    return Number.isInteger(v)
      ? v.toLocaleString()
      : v.toLocaleString(undefined, { maximumFractionDigits: 4 });
  }
  return String(v);
};

const ImportPreviewTable = ({ preview }) => {
  const columns = Array.isArray(preview?.columns) ? preview.columns : [];
  const rows = Array.isArray(preview?.rows) ? preview.rows : [];
  if (!columns.length || !rows.length) return null;
  return (
    <Box sx={{ mt: 1.25 }}>
      <Typography sx={(t) => ({
        mb: 0.5, fontSize: 10, fontWeight: 700, letterSpacing: '0.07em',
        textTransform: 'uppercase', color: surfacesFor(t).faint,
      })}>
        Last {rows.length} rows
      </Typography>
      <TableContainer
        sx={(t) => {
          const s = surfacesFor(t);
          return {
            borderRadius: '10px',
            border: `1px solid ${s.border}`,
            bgcolor: s.panel,
            maxHeight: 300,
          };
        }}
      >
        <Table size="small" stickyHeader sx={{ width: 'max-content', minWidth: '100%' }}>
          <TableHead>
            <TableRow>
              {columns.map((c) => (
                <TableCell
                  key={c}
                  sx={(t) => {
                    const s = surfacesFor(t);
                    return {
                      bgcolor: s.panel,
                      py: 0.6, px: 1.25,
                      fontSize: 10, fontWeight: 600,
                      textTransform: 'uppercase', letterSpacing: 0.5,
                      whiteSpace: 'nowrap',
                      color: s.faint,
                      borderBottom: `1px solid ${s.border}`,
                    };
                  }}
                >
                  {c}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={i} sx={{ '&:last-child td': { borderBottom: 'none' } }}>
                {columns.map((c, j) => {
                  const val = fmtPreviewCell(r[j]);
                  return (
                    <TableCell
                      key={c}
                      title={val}
                      sx={(t) => {
                        const s = surfacesFor(t);
                        return {
                          py: 0.55, px: 1.25,
                          fontSize: 11.5, color: s.text,
                          whiteSpace: 'nowrap',
                          maxWidth: 180,
                          overflow: 'hidden', textOverflow: 'ellipsis',
                          borderBottom: `1px solid ${s.border}`,
                        };
                      }}
                    >
                      {val}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const ImportReceipt = ({ receipt, preview }) => (
  <Box sx={{ mt: 1.5, ...enterSx }}>
    {/* Unmissable completion receipt, with the tail of the data itself. */}
    <Box
      sx={(t) => {
        const accent = accentOf(t);
        return {
          p: '10px 12px', borderRadius: '12px',
          border: `1px solid ${alpha(accent, 0.4)}`,
          bgcolor: alpha(accent, t.palette.mode === 'dark' ? 0.08 : 0.05),
        };
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <CheckCircleIcon sx={(t) => ({ fontSize: 18, color: accentOf(t), flexShrink: 0 })} />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={(t) => ({ fontSize: 12.5, fontWeight: 750, color: surfacesFor(t).text })}>
            Import complete
          </Typography>
          <Typography sx={(t) => ({ fontSize: 11.5, color: surfacesFor(t).muted })}>
            {receipt}
          </Typography>
        </Box>
      </Stack>
      <ImportPreviewTable preview={preview} />
    </Box>
  </Box>
);


// ---------------------------------------------------------------------------

// The three ways data gets in — presented as equals. Their own data is
// what we actually want; samples are the zero-friction fallback.
const DATA_PATHS = [
  { id: 'upload',  label: 'Upload a CSV',          desc: 'One row per time period works best', Icon: UploadIcon },
  { id: 'connect', label: 'Connect a live source', desc: 'Postgres, S3, BigQuery, Kafka, REST', Icon: PlugIcon },
  { id: 'sample',  label: 'Load a sample dataset', desc: 'Two years of realistic data, in seconds', Icon: DatabaseIcon },
];

const SamplePickerBlock = ({ spec, interactive }) => {
  const { enabled, onSend } = interactive || {};
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { openDock } = useAnalystDock();
  const slice = useSelector((s) => s.datasource_load_sample) || {};
  const job = slice.response; // {useCaseId, status, progress, name, rows, error, preview}
  const [mode, setMode] = useState(null); // null | 'upload' | 'sample'
  const [pickedId, setPickedId] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [uploadErr, setUploadErr] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);
  const sentRef = useRef(false);

  // The FIRST curated match leads the sample list — a hero card with its
  // description. The rest of the catalog stays one click away so a domain
  // filter never locks users out of other samples.
  const wanted = Array.isArray(spec?.use_case_ids) && spec.use_case_ids.length
    ? spec.use_case_ids : null;
  const lead = wanted
    ? wanted.map((id) => USE_CASES.find((u) => u.id === id)).filter(Boolean)
    : USE_CASES;
  const rest = wanted
    ? USE_CASES.filter((u) => !wanted.includes(u.id))
    : [];
  const hero = wanted ? lead[0] || null : null;
  const gridAll = [...lead, ...rest].filter((u) => !hero || u.id !== hero.id);
  const collapsedCount = hero ? Math.max(lead.length - 1, 0) : 8;
  const visible = showAll || gridAll.length <= collapsedCount + 1
    ? gridAll : gridAll.slice(0, collapsedCount);

  const mine = pickedId && job?.useCaseId === pickedId ? job : null;
  const busy = Boolean(mine && (mine.status === 'uploading' || mine.status === 'processing'));
  const [doneInfo, setDoneInfo] = useState(null);

  // A sample import finished: show the receipt and hand off to the
  // assistant, which confirms the target (from real columns) and builds
  // the baseline. The recommendation is a suggestion, not a commitment.
  useEffect(() => {
    if (!mine || sentRef.current) return;
    if (mine.status === 'completed') {
      sentRef.current = true;
      const uc = getUseCaseById(pickedId);
      const rows = mine.rows ? `${Number(mine.rows).toLocaleString()} rows` : 'all rows';
      dispatch(clearLoadSample());
      setDoneInfo({
        receipt: `${uc.label} — ${rows} from ${mine.name}`,
        preview: mine.preview || null,
      });
      onSend?.(
        `I loaded the "${uc.label}" sample dataset (${rows} from ${mine.name}). `
        + `Recommended target: ${uc.sample.targetColumn}.`,
      );
    }
  }, [mine, pickedId, dispatch, onSend]);

  const live = enabled && !busy && !doneInfo;

  const pick = (id) => {
    if (!live) return;
    setPickedId(id);
    sentRef.current = false;
    dispatch(loadSampleRequest({ useCaseId: id }));
  };

  const choosePath = (id) => {
    if (!live) return;
    if (id === 'connect') {
      // The Data Sources page owns connector setup; the chat rides along
      // docked and picks the thread back up when the import lands.
      onSend?.("I'd rather connect a live data source — I'm on the Data Sources page now.");
      openDock();
      navigate('/datasources');
      return;
    }
    setMode((prev) => (prev === id ? null : id));
  };

  // Upload path: same contract as the composer — the app imports the
  // file and posts the receipt as the user's message.
  const handleFiles = (fileList) => {
    const file = Array.from(fileList || []).filter(Boolean)[0];
    if (!file) return;
    if (!/\.csv$/i.test(file.name)) {
      setUploadErr(`${file.name} isn't a CSV — only .csv files are supported here.`);
      return;
    }
    setUploadErr(null);
    onSend?.(`Uploaded ${file.name}.`, [file]);
  };

  const heroPicked = hero && pickedId === hero.id;
  const heroLoading = heroPicked && busy;
  const heroFailed = heroPicked && mine?.status === 'failed';

  const tileSx = (isPicked) => (t) => {
    const s = surfacesFor(t);
    const accent = accentOf(t);
    return {
      p: '11px 12px', borderRadius: '13px',
      border: `1.5px solid ${isPicked ? accent : s.border}`,
      bgcolor: isPicked ? alpha(accent, 0.07) : s.panel,
      cursor: live ? 'pointer' : 'default',
      opacity: !enabled || (doneInfo && !isPicked) ? 0.5 : 1,
      userSelect: 'none',
      transition: 'all 130ms ease',
      ...(live ? {
        '&:hover': {
          borderColor: accent,
          transform: 'translateY(-1px)',
        },
      } : {}),
    };
  };

  return (
    <Box sx={{ my: 1, ...enterSx }}>
      <PromptLine>{spec?.prompt || 'Get your data in — pick whichever path fits'}</PromptLine>

      {/* Three equal paths. No hierarchy: your data and sample data are
          the same size, the same weight, one click apart. */}
      {!doneInfo && (
        <Box sx={{
          display: 'grid', gap: 1,
          gridTemplateColumns: 'repeat(auto-fit, minmax(165px, 1fr))',
        }}>
          {DATA_PATHS.map(({ id, label, desc, Icon }) => {
            const active = mode === id;
            return (
              <Box
                key={id}
                role="button"
                tabIndex={live ? 0 : -1}
                onClick={() => choosePath(id)}
                onKeyDown={(e) => { if (e.key === 'Enter') choosePath(id); }}
                sx={tileSx(active)}
              >
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <Box sx={(t) => {
                    const accent = accentOf(t);
                    return {
                      width: 28, height: 28, borderRadius: '9px', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      bgcolor: alpha(accent, t.palette.mode === 'dark' ? 0.15 : 0.09),
                      color: accent,
                    };
                  }}>
                    <Icon sx={{ fontSize: 15 }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={(t) => ({
                      fontSize: 12.5, fontWeight: 700, lineHeight: 1.3,
                      color: surfacesFor(t).text,
                    })}>
                      {label}
                    </Typography>
                    <Typography sx={(t) => ({
                      mt: 0.25, fontSize: 10.5, lineHeight: 1.35,
                      color: surfacesFor(t).muted,
                    })}>
                      {desc}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Upload path: inline drop zone, no extra round trip. */}
      {mode === 'upload' && !doneInfo && (
        <Box sx={{ mt: 1, maxWidth: 440 }}>
          <Box
            onClick={live ? () => fileRef.current?.click() : undefined}
            onDragOver={live ? (e) => { e.preventDefault(); setDragOver(true); } : undefined}
            onDragLeave={live ? () => setDragOver(false) : undefined}
            onDrop={live ? (e) => {
              e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files);
            } : undefined}
            sx={(t) => {
              const s = surfacesFor(t);
              const accent = accentOf(t);
              return {
                p: '18px 16px', textAlign: 'center',
                borderRadius: '13px',
                border: `1.5px dashed ${dragOver ? accent : s.border2}`,
                bgcolor: dragOver ? alpha(accent, 0.06) : s.panel,
                cursor: live ? 'pointer' : 'default',
                transition: 'all 130ms',
                ...(live ? { '&:hover': { borderColor: accent } } : {}),
              };
            }}
          >
            <input
              ref={fileRef} type="file" hidden accept=".csv"
              onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
            />
            <UploadIcon sx={(t) => ({ fontSize: 24, color: accentOf(t), mb: 0.5 })} />
            <Typography sx={(t) => ({ fontSize: 12.5, fontWeight: 700, color: surfacesFor(t).text })}>
              Drop your CSV here
            </Typography>
            <Typography sx={(t) => ({ mt: 0.25, fontSize: 11, color: surfacesFor(t).muted })}>
              or click to browse — it imports automatically
            </Typography>
          </Box>
          {uploadErr && (
            <Typography sx={{ mt: 0.75, fontSize: 11.5, color: 'error.main' }}>{uploadErr}</Typography>
          )}
        </Box>
      )}

      {/* Sample path: recommended dataset leads, catalog one click away. */}
      {mode === 'sample' && (
        <Box sx={{ mt: 1 }}>
          {hero && !doneInfo && (
            <Box
              role="button"
              tabIndex={live ? 0 : -1}
              onClick={() => pick(hero.id)}
              onKeyDown={(e) => { if (e.key === 'Enter') pick(hero.id); }}
              sx={(t) => ({
                ...tileSx(heroPicked)(t),
                p: '14px 16px', mb: 1, borderRadius: '14px',
              })}
            >
              <Stack direction="row" spacing={1.25} alignItems="flex-start">
                <Box sx={(t) => {
                  const accent = accentOf(t);
                  return {
                    width: 34, height: 34, borderRadius: '10px', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: alpha(accent, t.palette.mode === 'dark' ? 0.16 : 0.1),
                    color: accent,
                  };
                }}>
                  {heroLoading
                    ? <CircularProgress size={15} color="inherit" />
                    : <hero.Icon sx={{ fontSize: 17 }} />}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.25 }}>
                    <Typography sx={(t) => ({ fontSize: 13, fontWeight: 750, color: surfacesFor(t).text })}>
                      {hero.label}
                    </Typography>
                    <Chip
                      size="small" label="Best match for your domain"
                      sx={(t) => ({
                        height: 16, fontSize: 9, fontWeight: 700,
                        bgcolor: alpha(accentOf(t), 0.14), color: accentOf(t),
                      })}
                    />
                  </Stack>
                  {hero.sample?.description && (
                    <Typography sx={(t) => ({
                      mt: 0.25, fontSize: 11, lineHeight: 1.45, color: surfacesFor(t).muted,
                    })}>
                      {hero.sample.description}
                    </Typography>
                  )}
                  {heroLoading && (
                    <LinearProgress
                      variant={mine?.progress ? 'determinate' : 'indeterminate'}
                      value={mine?.progress || 0}
                      sx={{ mt: 1, height: 4, borderRadius: 2 }}
                    />
                  )}
                  {heroFailed && (
                    <Typography sx={{ mt: 0.5, fontSize: 10.5, color: 'error.main' }}>
                      {mine.error || 'Import failed'} — click to retry
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Box>
          )}

          {!doneInfo && visible.length > 0 && (
            <Box sx={{
              display: 'grid', gap: 1,
              // Container-driven so the picker works at dock width too.
              gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))',
            }}>
              {visible.map((uc) => {
                const isPicked = pickedId === uc.id;
                const loading = isPicked && busy;
                const failed = isPicked && mine?.status === 'failed';
                return (
                  <Box
                    key={uc.id}
                    role="button"
                    tabIndex={live ? 0 : -1}
                    onClick={() => pick(uc.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter') pick(uc.id); }}
                    sx={tileSx(isPicked)}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={(t) => {
                        const accent = accentOf(t);
                        return {
                          width: 26, height: 26, borderRadius: '8px', flexShrink: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          bgcolor: alpha(accent, t.palette.mode === 'dark' ? 0.15 : 0.09),
                          color: accent,
                        };
                      }}>
                        {loading
                          ? <CircularProgress size={13} color="inherit" />
                          : <uc.Icon sx={{ fontSize: 14 }} />}
                      </Box>
                      <Typography noWrap sx={(t) => ({
                        fontSize: 12, fontWeight: 650, color: surfacesFor(t).text,
                      })}>
                        {uc.label}
                      </Typography>
                    </Stack>
                    {loading && (
                      <LinearProgress
                        variant={mine?.progress ? 'determinate' : 'indeterminate'}
                        value={mine?.progress || 0}
                        sx={{ mt: 1, height: 4, borderRadius: 2 }}
                      />
                    )}
                    {failed && (
                      <Typography sx={{ mt: 0.5, fontSize: 10.5, color: 'error.main' }}>
                        {mine.error || 'Import failed'} — click to retry
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}

          {!showAll && !doneInfo && gridAll.length > visible.length && (
            <Typography
              role="button" tabIndex={0}
              onClick={() => setShowAll(true)}
              onKeyDown={(e) => { if (e.key === 'Enter') setShowAll(true); }}
              sx={(t) => ({
                mt: 1, display: 'inline-block', fontSize: 11.5, fontWeight: 600,
                color: accentOf(t), cursor: 'pointer', userSelect: 'none',
              })}
            >
              {hero ? `More sample datasets (${gridAll.length})` : `Show all ${gridAll.length} sample datasets`}
            </Typography>
          )}
        </Box>
      )}

      {doneInfo && (
        <ImportReceipt receipt={doneInfo.receipt} preview={doneInfo.preview} />
      )}
    </Box>
  );
};


// ---------------------------------------------------------------------------
// connector_setup — guided credentials + auto-mapped import
// ---------------------------------------------------------------------------

const SOURCE_META = {
  postgres: {
    label: 'PostgreSQL',
    placeholder: 'postgresql://user:password@host:5432/database',
    help: 'Read-only credentials are enough — we introspect tables and auto-map columns.',
  },
  mysql: {
    label: 'MySQL',
    placeholder: 'mysql://user:password@host:3306/database',
    help: 'Read-only credentials are enough — we introspect tables and auto-map columns.',
  },
  s3: {
    label: 'Amazon S3',
    placeholder: 's3://bucket/prefix',
    help: 'CSV / Parquet / JSON files are grouped by pattern and imported on a schedule.',
    fields: [
      { key: 'access_key_id', label: 'Access key ID' },
      { key: 'secret_access_key', label: 'Secret access key', secret: true },
      { key: 'region', label: 'Region (optional)', optional: true },
    ],
  },
  kafka: {
    label: 'Kafka',
    placeholder: 'kafka://broker1:9092,broker2:9092/topic',
    help: 'We sample the topic to detect the message schema.',
    fields: [
      { key: 'username', label: 'SASL username (optional)', optional: true },
      { key: 'password', label: 'SASL password (optional)', secret: true, optional: true },
    ],
  },
  bigquery: {
    label: 'BigQuery',
    placeholder: '',
    help: 'Paste a service-account JSON key with BigQuery read access.',
    json: true,
  },
};

// These need decisions (pagination, sync modes, auth flows) that the
// full page walks through properly — the chat just points there.
const FULL_PAGE_TYPES = new Set(['rest', 'other']);

// Mirror ConnectData's create-body table shape from an inspected table,
// trusting the auto-mapping (chat = the fast path; full page = control).
const toCreateTable = (t) => ({
  table_name: t.table_name,
  frequency: 'hourly',
  timestamp_column: t.timestamp_column,
  entity_column: t.entity_column,
  watermark_column: t.watermark_column,
  watermark_type: t.watermark_type,
  rest: t.rest || false,
  array_path: t.array_path,
  shape: t.shape,
  encoding: t.encoding,
  ...(t.s3 ? { s3: t.s3 } : {}),
  ...(t.kafka ? { kafka: t.kafka } : {}),
  field_mappings: (t.columns || []).filter((c) => c.included || c.is_event).map((c) => ({
    source_field: c.source_field,
    target_key: c.target_key,
    data_type: c.data_type,
    display_name: c.display_name || undefined,
    is_timestamp: c.is_timestamp,
    is_entity: c.is_entity,
    is_event: c.is_event || false,
    parse_options: {},
  })),
});

const fieldSx = { '& .MuiInputBase-input': { fontSize: 12.5 } };

const ConnectorSetupBlock = ({ spec, interactive }) => {
  const { enabled, onSend } = interactive || {};
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const sourceType = spec?.source_type || 'other';
  const meta = SOURCE_META[sourceType];

  const inspectSlice = useSelector((s) => s.datasource_connect_inspect) || {};
  const createSlice = useSelector((s) => s.datasource_connect_create) || {};

  // phase: form -> inspecting -> tables -> creating -> done
  const [phase, setPhase] = useState('form');
  const [connString, setConnString] = useState('');
  const [saJson, setSaJson] = useState('');
  const [extra, setExtra] = useState({});
  const [error, setError] = useState(null);
  const [inspectResult, setInspectResult] = useState(null);
  const [selected, setSelected] = useState({});
  const [importHistory, setImportHistory] = useState(true);
  // The inspect/create slices are shared with the full connectors page;
  // this ref makes sure we only consume responses we asked for.
  const activeRef = useRef(false);
  const bodyRef = useRef(null);

  const buildBody = () => {
    const body = { connector_type: sourceType };
    if (connString.trim()) body.connection_string = connString.trim();
    if (sourceType === 'bigquery' && saJson.trim()) body.service_account_json = saJson.trim();
    const creds = {};
    ['username', 'password', 'access_key_id', 'secret_access_key'].forEach((k) => {
      if (extra[k]) creds[k] = extra[k];
    });
    const conn = {};
    if (sourceType === 's3' && extra.region) conn.region = extra.region;
    if (Object.keys(creds).length) body.credentials = creds;
    if (Object.keys(conn).length) body.connection = conn;
    return body;
  };

  const canInspect = sourceType === 'bigquery' ? saJson.trim() : connString.trim();

  const runInspect = () => {
    if (!canInspect) return;
    const body = buildBody();
    bodyRef.current = body;
    activeRef.current = true;
    setError(null);
    setPhase('inspecting');
    dispatch(clearConnectInspect());
    dispatch(connectInspectRequest({ body }));
  };

  // Consume the inspect response (only if this block initiated it).
  useEffect(() => {
    if (!activeRef.current || phase !== 'inspecting') return;
    if (inspectSlice.error) {
      setError(inspectSlice.error);
      setPhase('form');
      activeRef.current = false;
      dispatch(clearConnectInspect());
      return;
    }
    const data = inspectSlice.response;
    if (!data) return;
    activeRef.current = false;
    dispatch(clearConnectInspect());
    if (data.status === 'failed' || data.test?.success === false) {
      setError(data.test?.error || data.error || 'Connection test failed — check the credentials.');
      setPhase('form');
      return;
    }
    if (data.created) {
      // Nothing to map (e.g. empty source) — the saga already created it.
      setPhase('done');
      onSend?.(`I connected ${data.label || meta?.label || sourceType} — no tables to import yet.`);
      return;
    }
    const tbls = Array.isArray(data.tables) ? data.tables : [];
    setInspectResult(data);
    setSelected(Object.fromEntries(tbls.map((t) => [t.table_name, Boolean(t.timestamp_column)])));
    setPhase('tables');
  }, [inspectSlice, phase, dispatch, onSend, meta, sourceType]);

  const chosenNames = inspectResult?.tables
    ? inspectResult.tables.filter((t) => selected[t.table_name]).map((t) => t.table_name)
    : [];

  const runCreate = () => {
    if (!chosenNames.length) return;
    const tables = inspectResult.tables
      .filter((t) => selected[t.table_name])
      .map((t) => toCreateTable(t));
    activeRef.current = true;
    setError(null);
    setPhase('creating');
    dispatch(clearConnectCreate());
    dispatch(connectCreateRequest({
      body: {
        ...bodyRef.current,
        name: inspectResult.label,
        import_historical: importHistory,
        tables,
      },
    }));
  };

  useEffect(() => {
    if (!activeRef.current || phase !== 'creating') return;
    if (createSlice.error) {
      setError(createSlice.error);
      setPhase('tables');
      activeRef.current = false;
      dispatch(clearConnectCreate());
      return;
    }
    const out = createSlice.response;
    if (!out) return;
    activeRef.current = false;
    dispatch(clearConnectCreate());
    setPhase('done');
    onSend?.(
      `I connected ${inspectResult?.label || meta?.label || sourceType} and started importing `
      + `${chosenNames.length} ${chosenNames.length === 1 ? 'table' : 'tables'} `
      + `(${chosenNames.join(', ')}). Historical backfill is ${importHistory ? 'on' : 'off'}.`,
    );
  }, [createSlice, phase, dispatch, onSend, inspectResult, meta, sourceType, chosenNames, importHistory]);

  const wrap = (children) => (
    <Box
      sx={(t) => {
        const s = surfacesFor(t);
        return {
          my: 1, p: '14px 16px', maxWidth: 480,
          borderRadius: '14px',
          border: `1px solid ${s.border}`,
          bgcolor: s.panel,
          opacity: enabled || phase === 'done' ? 1 : 0.55,
          ...enterSx,
        };
      }}
    >
      {children}
    </Box>
  );

  // REST / anything exotic: hand off to the full guided page.
  if (FULL_PAGE_TYPES.has(sourceType) || !meta) {
    return wrap(
      <Stack spacing={1}>
        <Typography sx={(t) => ({ fontSize: 13, fontWeight: 700, color: surfacesFor(t).text })}>
          {spec?.prompt || 'Connect a data source'}
        </Typography>
        <Typography sx={(t) => ({ fontSize: 11.5, color: surfacesFor(t).muted })}>
          {sourceType === 'rest'
            ? 'REST APIs need a couple of choices (auth, pagination, sync) — the guided connector page walks you through them in about a minute.'
            : 'The connectors page has guided setup for every source — databases, warehouses, S3, Kafka, REST APIs and one-click apps.'}
        </Typography>
        <Button
          size="small" variant="outlined" endIcon={<OpenInNewIcon sx={{ fontSize: 13 }} />}
          onClick={() => navigate('/datasources/connect')}
          disabled={!enabled}
          sx={{ alignSelf: 'flex-start', fontSize: 12, textTransform: 'none' }}
        >
          Open the connectors page
        </Button>
      </Stack>,
    );
  }

  if (phase === 'done') {
    return wrap(
      <Stack direction="row" spacing={1} alignItems="center">
        <CheckCircleIcon sx={(t) => ({ fontSize: 18, color: accentOf(t) })} />
        <Typography sx={(t) => ({ fontSize: 12.5, fontWeight: 600, color: surfacesFor(t).text })}>
          {meta.label} connected
        </Typography>
      </Stack>,
    );
  }

  if (phase === 'tables' || phase === 'creating') {
    const tbls = inspectResult?.tables || [];
    return wrap(
      <Stack spacing={1.25}>
        <Stack direction="row" spacing={1} alignItems="center">
          <CheckCircleIcon sx={(t) => ({ fontSize: 16, color: accentOf(t) })} />
          <Typography sx={(t) => ({ fontSize: 12.5, fontWeight: 700, color: surfacesFor(t).text })}>
            {inspectResult?.label || meta.label} — pick what to import
          </Typography>
        </Stack>
        <Stack spacing={0.5} sx={{ maxHeight: 220, overflowY: 'auto' }}>
          {tbls.map((t) => {
            const noTs = !t.timestamp_column;
            return (
              <Stack
                key={t.table_name}
                direction="row" alignItems="center" spacing={0.75}
                sx={(th) => ({
                  px: 1, py: 0.4, borderRadius: '9px',
                  border: `1px solid ${surfacesFor(th).border}`,
                  opacity: noTs ? 0.55 : 1,
                })}
              >
                <Checkbox
                  size="small"
                  checked={Boolean(selected[t.table_name])}
                  disabled={noTs || phase === 'creating' || !enabled}
                  onChange={(e) => setSelected((prev) => ({
                    ...prev, [t.table_name]: e.target.checked,
                  }))}
                  sx={{ p: 0.4 }}
                />
                <Typography noWrap sx={(th) => ({
                  flex: 1, fontSize: 12, fontWeight: 600, color: surfacesFor(th).text,
                })}>
                  {t.table_name}
                </Typography>
                <Typography sx={(th) => ({ fontSize: 10.5, color: surfacesFor(th).faint })}>
                  {noTs
                    ? 'no time column'
                    : `${(t.columns || []).length} cols · time: ${t.timestamp_column}`}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Switch
            size="small"
            checked={importHistory}
            disabled={phase === 'creating' || !enabled}
            onChange={(e) => setImportHistory(e.target.checked)}
          />
          <Typography sx={(t) => ({ fontSize: 11.5, color: surfacesFor(t).muted })}>
            Import historical data
          </Typography>
        </Stack>
        {error && (
          <Typography sx={{ fontSize: 11.5, color: 'error.main' }}>{error}</Typography>
        )}
        <Button
          size="small" variant="contained" disableElevation
          onClick={runCreate}
          disabled={!enabled || !chosenNames.length || phase === 'creating'}
          startIcon={phase === 'creating' ? <CircularProgress size={12} color="inherit" /> : null}
          sx={{ alignSelf: 'flex-start', fontSize: 12, textTransform: 'none' }}
        >
          {phase === 'creating' ? 'Connecting…' : `Import ${chosenNames.length} ${chosenNames.length === 1 ? 'table' : 'tables'}`}
        </Button>
      </Stack>,
    );
  }

  // phase === 'form' | 'inspecting'
  const busy = phase === 'inspecting';
  return wrap(
    <Stack spacing={1.25}>
      <Stack direction="row" spacing={1} alignItems="center">
        <PlugIcon sx={(t) => ({ fontSize: 16, color: accentOf(t) })} />
        <Typography sx={(t) => ({ fontSize: 12.5, fontWeight: 700, color: surfacesFor(t).text })}>
          {spec?.prompt || `Connect ${meta.label}`}
        </Typography>
      </Stack>
      <Typography sx={(t) => ({ fontSize: 11, color: surfacesFor(t).muted, mt: '-6px !important' })}>
        {meta.help} Credentials go straight to the connector — never through the assistant.
      </Typography>
      {meta.json ? (
        <TextField
          size="small" fullWidth multiline minRows={3} maxRows={6}
          placeholder='{ "type": "service_account", ... }'
          value={saJson}
          onChange={(e) => setSaJson(e.target.value)}
          disabled={!enabled || busy}
          InputProps={{ sx: { fontFamily: 'monospace', fontSize: 11.5 } }}
        />
      ) : (
        <TextField
          size="small" fullWidth
          placeholder={meta.placeholder}
          value={connString}
          onChange={(e) => setConnString(e.target.value)}
          disabled={!enabled || busy}
          sx={fieldSx}
        />
      )}
      {(meta.fields || []).map((f) => (
        <TextField
          key={f.key}
          size="small" fullWidth
          type={f.secret ? 'password' : 'text'}
          label={f.label}
          value={extra[f.key] || ''}
          onChange={(e) => setExtra((prev) => ({ ...prev, [f.key]: e.target.value }))}
          disabled={!enabled || busy}
          sx={fieldSx}
        />
      ))}
      {error && (
        <Stack direction="row" spacing={0.75} alignItems="flex-start">
          <ErrorIcon sx={{ fontSize: 14, mt: '1px', color: 'error.main' }} />
          <Typography sx={{ fontSize: 11.5, color: 'error.main' }}>{error}</Typography>
        </Stack>
      )}
      <Stack direction="row" spacing={1} alignItems="center">
        <Button
          size="small" variant="contained" disableElevation
          onClick={runInspect}
          disabled={!enabled || !canInspect || busy}
          startIcon={busy ? <CircularProgress size={12} color="inherit" /> : null}
          sx={{ fontSize: 12, textTransform: 'none' }}
        >
          {busy ? 'Testing connection…' : 'Test & inspect'}
        </Button>
        <Typography
          role="button" tabIndex={0}
          onClick={() => navigate('/datasources/connect')}
          onKeyDown={(e) => { if (e.key === 'Enter') navigate('/datasources/connect'); }}
          sx={(t) => ({
            fontSize: 11, color: surfacesFor(t).faint,
            cursor: 'pointer', userSelect: 'none',
            '&:hover': { color: surfacesFor(t).muted },
          })}
        >
          Need more control? Open the full page
        </Typography>
      </Stack>
    </Stack>,
  );
};


// ---------------------------------------------------------------------------
// config_form — an inline settings card: the assistant proposes defaults,
// the user adjusts anything and submits in one click. The submit becomes
// a structured user message ("Configured <title> — Label: value · ...")
// that the model reads to run the actual action.
// ---------------------------------------------------------------------------

const fmtNum = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return '0';
  return Number.isInteger(v) ? String(v) : String(Math.round(v * 100) / 100);
};

const SUMMARY_PREFIX = 'Configured ';

// Rebuild "Label: value" pairs from a prior receipt message so a
// reloaded thread still shows what was chosen (values live only in
// component state at submit time).
const parseSummary = (text) => {
  const body = String(text || '').replace(/^Configured .*? — /, '');
  const out = {};
  body.split(' · ').forEach((pair) => {
    const idx = pair.indexOf(': ');
    if (idx > 0) out[pair.slice(0, idx)] = pair.slice(idx + 2).replace(/ \[[^\]]*\]$/, '');
  });
  return out;
};

const ConfigSelect = ({ field, value, live, onChange }) => (
  <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
    {(field.options || []).map((o) => {
      const picked = o.value === value;
      return (
        <Box
          key={o.value}
          role="button"
          tabIndex={live ? 0 : -1}
          onClick={live ? () => onChange(o.value) : undefined}
          onKeyDown={(e) => { if (live && e.key === 'Enter') onChange(o.value); }}
          sx={(t) => {
            const s = surfacesFor(t);
            const accent = accentOf(t);
            return {
              px: 1.5, py: 0.55, borderRadius: '10px',
              fontSize: 12, fontWeight: picked ? 700 : 500,
              border: `1.5px solid ${picked ? accent : s.border2}`,
              bgcolor: picked ? alpha(accent, t.palette.mode === 'dark' ? 0.13 : 0.07) : 'transparent',
              color: picked ? accent : s.muted,
              cursor: live ? 'pointer' : 'default',
              userSelect: 'none',
              transition: 'all 120ms',
              ...(live && !picked ? { '&:hover': { borderColor: accent, color: accent } } : {}),
            };
          }}
        >
          {o.label}
        </Box>
      );
    })}
  </Stack>
);

const ConfigNumber = ({ field, value, live, onChange }) => {
  const step = Number(field.step) > 0 ? Number(field.step) : 1;
  const clamp = (v) => {
    let n = Number.isFinite(Number(v)) ? Number(v) : 0;
    if (field.min != null) n = Math.max(n, field.min);
    if (field.max != null) n = Math.min(n, field.max);
    return n;
  };
  const bump = (dir) => onChange(clamp(Number(value || 0) + dir * step));
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <IconButton
        size="small" disabled={!live || (field.min != null && value <= field.min)}
        onClick={() => bump(-1)}
        sx={(t) => ({
          width: 26, height: 26, borderRadius: '8px',
          border: `1px solid ${surfacesFor(t).border2}`,
        })}
      >
        <RemoveIcon sx={{ fontSize: 13 }} />
      </IconButton>
      <TextField
        size="small" type="number" value={value}
        disabled={!live}
        onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
        onBlur={() => onChange(clamp(value))}
        inputProps={{ min: field.min, max: field.max, step }}
        sx={{
          width: 88,
          '& .MuiInputBase-input': { fontSize: 12.5, py: 0.5, textAlign: 'center' },
        }}
      />
      <IconButton
        size="small" disabled={!live || (field.max != null && value >= field.max)}
        onClick={() => bump(1)}
        sx={(t) => ({
          width: 26, height: 26, borderRadius: '8px',
          border: `1px solid ${surfacesFor(t).border2}`,
        })}
      >
        <AddIcon sx={{ fontSize: 13 }} />
      </IconButton>
      {field.unit && (
        <Typography sx={(t) => ({ fontSize: 11.5, color: surfacesFor(t).muted })}>
          {field.unit}
        </Typography>
      )}
    </Stack>
  );
};

const ConfigFormBlock = ({ spec, interactive }) => {
  const { enabled, chosenText, onSend } = interactive || {};
  const fields = Array.isArray(spec?.fields) ? spec.fields : [];
  const [values, setValues] = useState(
    () => Object.fromEntries(fields.map((f) => [f.id, f.default])),
  );
  const [submitted, setSubmitted] = useState(false);

  // A reloaded thread: the user's next message is this form's receipt
  // (or its skip label) — freeze accordingly.
  const receipt = (chosenText || '').startsWith(SUMMARY_PREFIX) ? chosenText : null;
  const skipped = Boolean(spec?.skip_label) && chosenText === spec.skip_label;
  const done = submitted || Boolean(receipt) || skipped;
  const live = Boolean(enabled) && !done;

  if (!fields.length) return null;

  const set = (id, v) => setValues((prev) => ({ ...prev, [id]: v }));

  // What the model reads: keep the machine value alongside the label
  // when they differ (e.g. a signal's display name vs its raw key).
  const sendValue = (f, v) => {
    if (f.type === 'toggle') return v ? 'on' : 'off';
    if (f.type === 'select') {
      const o = (f.options || []).find((x) => x.value === v) || {};
      const label = o.label || String(v ?? '');
      return o.value != null && o.value !== label ? `${label} [${o.value}]` : label;
    }
    if (f.type === 'number' || f.type === 'slider') {
      return `${fmtNum(v)}${f.unit ? ` ${f.unit}` : ''}`;
    }
    return String(v ?? '').trim() || '—';
  };
  // What the frozen card shows: labels only.
  const showValue = (f, v) => sendValue(f, v).replace(/ \[[^\]]*\]$/, '');

  const submit = () => {
    if (!live) return;
    setSubmitted(true);
    const parts = fields.map((f) => `${f.label}: ${sendValue(f, values[f.id])}`);
    onSend?.(`${SUMMARY_PREFIX}${spec?.title || 'settings'} — ${parts.join(' · ')}`);
  };

  const frozen = receipt ? parseSummary(receipt) : null;

  return (
    <Box
      sx={(t) => {
        const s = surfacesFor(t);
        return {
          my: 1, p: '14px 16px', maxWidth: 520,
          borderRadius: '14px',
          border: `1px solid ${s.border}`,
          bgcolor: s.panel,
          opacity: live || done ? 1 : 0.55,
          ...enterSx,
        };
      }}
    >
      {/* Header */}
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: done ? 1 : 1.75 }}>
        <Box sx={(t) => {
          const accent = accentOf(t);
          return {
            width: 28, height: 28, borderRadius: '9px', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: alpha(accent, t.palette.mode === 'dark' ? 0.15 : 0.09),
            color: accent,
          };
        }}>
          {done && !skipped
            ? <CheckCircleIcon sx={{ fontSize: 16 }} />
            : <TuneIcon sx={{ fontSize: 15 }} />}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={(t) => ({ fontSize: 13, fontWeight: 700, color: surfacesFor(t).text })}>
            {spec?.title || 'Settings'}
          </Typography>
          {!done && spec?.subtitle && (
            <Typography sx={(t) => ({ fontSize: 11, color: surfacesFor(t).muted })}>
              {spec.subtitle}
            </Typography>
          )}
          {skipped && (
            <Typography sx={(t) => ({ fontSize: 11, color: surfacesFor(t).muted })}>
              Skipped
            </Typography>
          )}
        </Box>
      </Stack>

      {/* Frozen: the chosen values, compact. */}
      {done && !skipped && (
        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
          {fields.map((f) => {
            const val = frozen
              ? (frozen[f.label] ?? showValue(f, f.default))
              : showValue(f, values[f.id]);
            return (
              <Box
                key={f.id}
                sx={(t) => {
                  const s = surfacesFor(t);
                  return {
                    px: 1.25, py: 0.5, borderRadius: '9px',
                    border: `1px solid ${s.border}`,
                    display: 'inline-flex', gap: 0.6, alignItems: 'baseline',
                  };
                }}
              >
                <Typography sx={(t) => ({ fontSize: 10.5, color: surfacesFor(t).faint })}>
                  {f.label}
                </Typography>
                <Typography sx={(t) => ({ fontSize: 11.5, fontWeight: 700, color: surfacesFor(t).text })}>
                  {val}
                </Typography>
              </Box>
            );
          })}
        </Stack>
      )}

      {/* Live (or dimmed-pending) form. */}
      {!done && (
        <>
          <Stack spacing={1.75}>
            {fields.map((f) => (
              <Box key={f.id}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: f.type === 'toggle' ? 0 : 0.75 }}>
                  <Typography sx={(t) => ({
                    fontSize: 11.5, fontWeight: 650, color: surfacesFor(t).text,
                    flex: f.type === 'toggle' ? 1 : 'none',
                  })}>
                    {f.label}
                  </Typography>
                  {f.type === 'slider' && (
                    <Typography sx={(t) => ({
                      fontSize: 11.5, fontWeight: 700, color: accentOf(t),
                    })}>
                      {fmtNum(values[f.id])}{f.unit ? ` ${f.unit}` : ''}
                    </Typography>
                  )}
                  {f.type === 'toggle' && (
                    <Switch
                      size="small"
                      checked={Boolean(values[f.id])}
                      disabled={!live}
                      onChange={(e) => set(f.id, e.target.checked)}
                    />
                  )}
                </Stack>
                {f.type === 'select' && (
                  <ConfigSelect field={f} value={values[f.id]} live={live} onChange={(v) => set(f.id, v)} />
                )}
                {f.type === 'slider' && (
                  <Slider
                    size="small"
                    value={Number(values[f.id]) || 0}
                    min={f.min} max={f.max} step={f.step || undefined}
                    disabled={!live}
                    onChange={(_, v) => set(f.id, v)}
                    sx={(t) => ({ color: accentOf(t), mx: 0.5, width: 'calc(100% - 8px)' })}
                  />
                )}
                {f.type === 'number' && (
                  <ConfigNumber field={f} value={values[f.id]} live={live} onChange={(v) => set(f.id, v)} />
                )}
                {f.type === 'text' && (
                  <TextField
                    size="small" fullWidth
                    value={values[f.id] ?? ''}
                    placeholder={f.placeholder || ''}
                    disabled={!live}
                    onChange={(e) => set(f.id, e.target.value)}
                    sx={fieldSx}
                  />
                )}
                {f.help && (
                  <Typography sx={(t) => ({ mt: 0.5, fontSize: 10.5, color: surfacesFor(t).faint })}>
                    {f.help}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mt: 2 }}>
            <Button
              size="small" variant="contained" disableElevation
              disabled={!live}
              onClick={submit}
              sx={{ fontSize: 12, textTransform: 'none', px: 2 }}
            >
              {spec?.submit_label || 'Apply'}
            </Button>
            {spec?.skip_label && (
              <Typography
                role="button" tabIndex={live ? 0 : -1}
                onClick={live ? () => { setSubmitted(true); onSend?.(spec.skip_label); } : undefined}
                onKeyDown={(e) => {
                  if (live && e.key === 'Enter') { setSubmitted(true); onSend?.(spec.skip_label); }
                }}
                sx={(t) => ({
                  fontSize: 11.5, fontWeight: 600, color: surfacesFor(t).faint,
                  cursor: live ? 'pointer' : 'default', userSelect: 'none',
                  '&:hover': live ? { color: surfacesFor(t).muted } : {},
                })}
              >
                {spec.skip_label}
              </Typography>
            )}
          </Stack>
        </>
      )}
    </Box>
  );
};


// ---------------------------------------------------------------------------
// correlation_bars — analyze_correlations output as signed strength
// bars, the same visual language as a goal's driver panel. Replaces the
// old markdown table: the eye reads sign + magnitude instantly.
// ---------------------------------------------------------------------------

const fmtR = (r) => {
  const v = Math.max(-1, Math.min(1, Number(r) || 0));
  return `${v > 0 ? '+' : v < 0 ? '\u2212' : ''}${Math.abs(v).toFixed(2)}`;
};

const CorrelationBarsBlock = ({ spec }) => {
  const items = Array.isArray(spec?.items) ? spec.items : [];
  if (!items.length) return null;
  return (
    <Box
      sx={(t) => {
        const s = surfacesFor(t);
        return {
          my: 1, p: '14px 16px', maxWidth: 480,
          borderRadius: '14px',
          border: `1px solid ${s.border}`,
          bgcolor: s.panel,
          ...enterSx,
        };
      }}
    >
      <Typography sx={(t) => ({
        fontSize: 11, fontWeight: 700, letterSpacing: '0.07em',
        textTransform: 'uppercase', color: surfacesFor(t).muted,
      })}>
        {spec?.title || `Correlation with ${spec?.target_label || 'the target'}`}
      </Typography>
      <Stack spacing={1.1} sx={{ mt: 1.5 }}>
        {items.map((it, i) => {
          const r = Math.max(-1, Math.min(1, Number(it.r) || 0));
          const positive = r >= 0;
          const width = Math.max(Math.abs(r) * 50, 1.5); // % of the track
          return (
            <Box key={`${it.signal}-${i}`}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box sx={{ width: '38%', minWidth: 0 }}>
                  <Typography noWrap title={it.signal} sx={(t) => ({
                    fontSize: 11.5, fontWeight: 650, fontFamily: 'monospace',
                    color: surfacesFor(t).text,
                  })}>
                    {it.signal}
                  </Typography>
                </Box>
                {/* Signed bar: grows right for positive, left for negative,
                    from a shared zero line — magnitude and direction in
                    one glance. */}
                <Box sx={(t) => ({
                  flex: 1, position: 'relative', height: 7,
                  borderRadius: 4,
                  bgcolor: t.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.05)' : 'rgba(40,50,35,0.07)',
                })}>
                  <Box sx={(t) => ({
                    position: 'absolute', left: '50%', top: -2, bottom: -2,
                    width: '1px', bgcolor: surfacesFor(t).border2,
                  })} />
                  <Box sx={(t) => {
                    const pos = t.palette.mode === 'dark' ? '#5ee6d0' : (t.brand?.teal || '#197563');
                    const neg = t.palette.mode === 'dark' ? '#f0819e' : '#be3455';
                    return {
                      position: 'absolute', top: 0, bottom: 0,
                      borderRadius: 4,
                      bgcolor: positive ? pos : neg,
                      ...(positive
                        ? { left: '50%', width: `${width}%` }
                        : { right: '50%', width: `${width}%` }),
                    };
                  }} />
                </Box>
                <Typography sx={(t) => ({
                  width: 44, textAlign: 'right', flexShrink: 0,
                  fontSize: 11.5, fontWeight: 700, fontFamily: 'monospace',
                  color: surfacesFor(t).text,
                })}>
                  {fmtR(r)}
                </Typography>
              </Stack>
              {it.note && (
                <Typography sx={(t) => ({
                  mt: 0.25, fontSize: 10.5, color: surfacesFor(t).faint,
                })}>
                  {it.note}
                </Typography>
              )}
            </Box>
          );
        })}
      </Stack>
      <Typography sx={(t) => ({ mt: 1.25, fontSize: 10.5, color: surfacesFor(t).faint })}>
        {spec?.caption || 'Candidates only — a goal build tests which ones truly predict.'}
      </Typography>
    </Box>
  );
};


// ---------------------------------------------------------------------------
// goal_overview — a compact factual card of what a goal build does for this
// target: drivers tested, models compared, champion served live. One box,
// three lines, no graphics; the agent asks in chat whether to build it.
// ---------------------------------------------------------------------------

const GoalOverviewBlock = ({ spec }) => {
  const target = spec?.target_label || 'your target';
  const drivers = (Array.isArray(spec?.driver_labels) ? spec.driver_labels : [])
    .filter(Boolean).slice(0, 3);
  const rows = [
    {
      id: 'drivers',
      label: 'Tests drivers',
      text: drivers.length
        ? `${drivers.join(', ')} — keeps only what improves the forecast`
        : 'your other signals — keeps only what improves the forecast',
    },
    {
      id: 'models',
      label: 'Compares models',
      text: 'statistical, tree-based, neural and foundation — best out-of-sample score wins',
    },
    {
      id: 'live',
      label: 'Serves it live',
      text: `${spec?.horizon_label || 'your horizon'}, with uncertainty bands — retrains as new data arrives`,
    },
  ];
  const foot = [
    spec?.credits ? `~${Math.round(spec.credits)} credits` : null,
    'builds in a few minutes',
  ].filter(Boolean).join(' · ');
  return (
    <Box
      sx={(t) => {
        const s = surfacesFor(t);
        return {
          my: 1, p: '12px 14px', maxWidth: 560,
          borderRadius: '14px',
          border: `1px solid ${s.border}`,
          bgcolor: s.panel,
          ...enterSx,
        };
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <TargetIcon sx={(t) => ({ fontSize: 15, color: accentOf(t) })} />
        <Typography sx={(t) => ({ fontSize: 12.5, fontWeight: 700, color: surfacesFor(t).text })}>
          A goal for {target}
        </Typography>
      </Stack>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1.25, rowGap: 0.6 }}>
        {rows.map((r) => (
          <React.Fragment key={r.id}>
            <Typography sx={(t) => ({
              fontSize: 11.5, fontWeight: 600, whiteSpace: 'nowrap',
              color: surfacesFor(t).text,
            })}>
              {r.label}
            </Typography>
            <Typography sx={(t) => ({ fontSize: 11.5, color: surfacesFor(t).muted })}>
              {r.text}
            </Typography>
          </React.Fragment>
        ))}
      </Box>
      <Typography sx={(t) => ({ mt: 1, fontSize: 10.5, color: surfacesFor(t).faint })}>
        {foot}
      </Typography>
    </Box>
  );
};


// ---------------------------------------------------------------------------
// goal_api — the goal is a real endpoint from day one: the exact curl
// that returns its live forecast, plus a jump to the Forecast page.
// ---------------------------------------------------------------------------

const goalApiCurl = (goalId) => {
  let ws = '';
  try { ws = localStorage.getItem('WORKSPACE_ID') || ''; } catch (_e) { /* private mode */ }
  const base = String(config.API_URL || 'https://api.predict.ai').replace(/\/+$/, '');
  return [
    `curl ${base}/v1/goals/${goalId}/forecast \\`,
    '  -H "Authorization: Bearer $PREDICTAI_API_KEY" \\',
    `  -H "X-Workspace-Id: ${ws || '<workspace-id>'}"`,
  ].join('\n');
};

const ApiSnippetBlock = ({ spec }) => {
  const navigate = useNavigate();
  const { collapseDock } = useAnalystDock();
  const [copied, setCopied] = useState(false);
  const goalId = spec?.goal_id;
  if (!goalId) return null;
  const cmd = goalApiCurl(goalId);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(cmd);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (_e) { /* clipboard unavailable */ }
  };

  const openForecast = () => {
    // Hand-off to the product UI: the page leads, the chat collapses.
    collapseDock();
    navigate(`/goals/${goalId}/forecast`);
  };

  return (
    <Box
      sx={(t) => {
        const s = surfacesFor(t);
        return {
          my: 1, p: '14px 16px', maxWidth: 560,
          borderRadius: '14px',
          border: `1px solid ${s.border}`,
          bgcolor: s.panel,
          ...enterSx,
        };
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <TerminalIcon sx={(t) => ({ fontSize: 15, color: accentOf(t) })} />
        <Typography sx={(t) => ({ fontSize: 12.5, fontWeight: 700, color: surfacesFor(t).text })}>
          {spec?.goal_name ? `${spec.goal_name} — live forecast API` : 'Live forecast API'}
        </Typography>
      </Stack>
      <Box sx={(t) => ({
        position: 'relative',
        p: '10px 12px', borderRadius: '10px',
        bgcolor: t.palette.mode === 'dark' ? '#0c0f0b' : '#151a14',
        border: `1px solid ${t.palette.mode === 'dark' ? '#232823' : '#2a302a'}`,
        overflowX: 'auto',
      })}>
        <IconButton
          size="small"
          onClick={copy}
          aria-label="Copy API command"
          sx={{
            position: 'absolute', top: 6, right: 6,
            color: copied ? '#c0fb6e' : 'rgba(233,237,231,0.55)',
            '&:hover': { color: '#c0fb6e' },
          }}
        >
          {copied ? <CheckIcon sx={{ fontSize: 14 }} /> : <CopyIcon sx={{ fontSize: 14 }} />}
        </IconButton>
        <Typography component="pre" sx={{
          m: 0, fontFamily: 'ui-monospace, Menlo, monospace',
          fontSize: 11, lineHeight: 1.6, color: '#d7e6c9',
          whiteSpace: 'pre',
        }}>
          {cmd}
        </Typography>
      </Box>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1.25, flexWrap: 'wrap', rowGap: 0.75 }}>
        <Button
          size="small" variant="outlined"
          endIcon={<ArrowIcon sx={{ fontSize: 13 }} />}
          onClick={openForecast}
          sx={{ fontSize: 12, textTransform: 'none' }}
        >
          Open the live forecast
        </Button>
        <Typography sx={(t) => ({ fontSize: 10.5, color: surfacesFor(t).faint })}>
          Returns the champion&apos;s latest forecast as JSON. API keys live in Settings.
        </Typography>
      </Stack>
    </Box>
  );
};


// ---------------------------------------------------------------------------
// Dispatcher
// ---------------------------------------------------------------------------

const InteractiveBlock = ({ kind, spec, interactive }) => {
  switch (kind) {
    case 'options':          return <OptionsBlock spec={spec} interactive={interactive} />;
    case 'file_drop':        return <FileDropBlock spec={spec} interactive={interactive} />;
    case 'checklist':        return <ChecklistBlock spec={spec} />;
    case 'sample_picker':    return <SamplePickerBlock spec={spec} interactive={interactive} />;
    case 'connector_setup':  return <ConnectorSetupBlock spec={spec} interactive={interactive} />;
    case 'config_form':      return <ConfigFormBlock spec={spec} interactive={interactive} />;
    case 'correlation_bars': return <CorrelationBarsBlock spec={spec} />;
    case 'goal_overview':
    case 'goal_pitch':       return <GoalOverviewBlock spec={spec} />;
    case 'goal_api':         return <ApiSnippetBlock spec={spec} />;
    default:                 return null;
  }
};

export default InteractiveBlock;
